import { useState } from 'react'
import { Alert, Button, Input, message, Space, Table, Tag, Typography } from 'antd'
import { logAnalyzeAI } from '@/api/logText'
import type { LogAnalyzeAIResult } from '@/types'

const { TextArea } = Input
const { Text } = Typography
const MONO = { fontFamily: "'JetBrains Mono', 'Consolas', monospace", fontSize: 12 }

const LEVEL_COLOR: Record<string, string> = {
  ERROR: 'red', FATAL: 'volcano', CRITICAL: 'volcano', WARN: 'orange', WARNING: 'orange', INFO: 'blue', DEBUG: 'default',
}

/** 日志智能分析页签：粘贴日志 → AI 提取错误、聚类、根因假设 */
export default function LogAnalyzeTab() {
  const [content, setContent] = useState('')
  const [focus, setFocus] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<LogAnalyzeAIResult | null>(null)

  /** AI 分析日志 */
  const handleAnalyze = async () => {
    if (!content.trim()) {
      message.warning('请粘贴日志内容')
      return
    }
    setLoading(true)
    try {
      setResult(await logAnalyzeAI({ content, focus }))
    } finally {
      setLoading(false)
    }
  }

  const errorColumns = [
    { title: '级别', dataIndex: 'level', width: 90, render: (v: string) => <Tag color={LEVEL_COLOR[v.toUpperCase()] ?? 'default'}>{v}</Tag> },
    { title: '错误摘要', dataIndex: 'message', ellipsis: true },
    { title: '次数', dataIndex: 'count', width: 70, align: 'right' as const },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 12, padding: '0 22px 12px', overflowY: 'auto' }}>
      <Alert
        type="info"
        showIcon
        message="粘贴日志后由 AI 提取错误、聚类同类日志并给出根因假设，结论仅供参考"
        style={{ flexShrink: 0 }}
      />
      <TextArea
        rows={9}
        placeholder="粘贴日志内容（服务日志、接口报错、构建输出等）"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        style={MONO}
      />
      <Space wrap style={{ flexShrink: 0 }}>
        <Input
          placeholder="关注点补充（可空），如：只关注登录失败相关"
          value={focus}
          onChange={(e) => setFocus(e.target.value)}
          style={{ width: 320 }}
        />
        <Button type="primary" style={{ background: 'var(--au-primary)', border: 'none' }} loading={loading} onClick={handleAnalyze}>
          AI 分析
        </Button>
      </Space>

      {result && !result.parsed && (
        <div style={{ flexShrink: 0 }}>
          <Tag color="warning">AI 返回格式未解析，原文：</Tag>
          <pre style={{ ...MONO, whiteSpace: 'pre-wrap', margin: '6px 0 0' }}>{result.raw}</pre>
        </div>
      )}
      {result?.parsed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 8 }}>
          {result.summary && <Alert type={result.errors?.length ? 'warning' : 'success'} showIcon message={result.summary} />}
          {(result.errors ?? []).length > 0 && (
            <div>
              <Text strong>提取的错误：</Text>
              <Table
                size="small"
                style={{ marginTop: 6 }}
                columns={errorColumns}
                dataSource={result.errors}
                rowKey={(_, i) => String(i)}
                pagination={false}
              />
            </div>
          )}
          {(result.clusters ?? []).length > 0 && (
            <div>
              <Text strong>日志聚类：</Text>
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {result.clusters!.map((c, i) => (
                  <div key={i}>
                    <Space wrap>
                      <Tag color="purple" style={MONO}>{c.pattern}</Tag>
                      <Tag color="blue">×{c.count}</Tag>
                    </Space>
                    <div><Text type="secondary">{c.description}</Text></div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(result.root_causes ?? []).length > 0 && (
            <div>
              <Text strong>根因假设：</Text>
              {result.root_causes!.map((r, i) => (
                <div key={i}><Text>· {r}</Text></div>
              ))}
            </div>
          )}
          {(result.suggestions ?? []).length > 0 && (
            <div>
              <Text strong>排查建议：</Text>
              {result.suggestions!.map((s, i) => (
                <div key={i}><Text>· {s}</Text></div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
